import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

export interface KidProfile {
  id: number;
  parent_id: number;
  nombre: string;
  avatar: string;
  fecha_nacimiento: string;
  tiempo_diario_minutos: number;
  activo: boolean;
  politica_aceptada: boolean;
  fecha_creacion: string;
  fecha_actualizacion: string;
  edad?: number;
}

export interface CreateKidProfileDTO {
  nombre: string;
  avatar: string;
  fecha_nacimiento: string;
  tiempo_diario_minutos: number;
  politica_aceptada: boolean;
}

export interface UpdateKidProfileDTO {
  nombre?: string;
  avatar?: string;
  fecha_nacimiento?: string;
  tiempo_diario_minutos?: number;
}

export interface KidStats {
  total_partidas: number;
  tiempo_total_segundos: number;
  juegos_jugados: number;
  tiempo_total_minutos: number;
}

export interface CanPlayResponse {
  can_play: boolean;
  reason?: string;
  tiempo_jugado: number;
  tiempo_limite: number;
  tiempo_restante?: number;
}

interface KidsStore {
  profiles: KidProfile[];
  selectedKid: KidProfile | null;
  isLoading: boolean;
  error: string | null;

  // Gestión de perfiles
  fetchProfiles: () => Promise<void>;
  fetchProfile: (id: number) => Promise<{ profile: KidProfile; stats: KidStats }>;
  createProfile: (data: CreateKidProfileDTO) => Promise<void>;
  updateProfile: (id: number, data: UpdateKidProfileDTO) => Promise<void>;
  deleteProfile: (id: number) => Promise<void>;

  // Selección de perfil
  selectKid: (id: number) => Promise<void>;
  getCurrentKid: () => Promise<void>;
  logoutKid: () => Promise<void>;

  // Control de tiempo y stats
  canPlay: (kidId: number) => Promise<CanPlayResponse>;
  registerPlayTime: (kidId: number, minutos: number) => Promise<void>;
  getStats: (kidId: number) => Promise<KidStats>;
  registerProgress: (kidId: number, data: any) => Promise<void>;

  // Utilidades
  clearError: () => void;
}

export const useKidsStore = create<KidsStore>()(persist((set, get) => ({
  profiles: [],
  selectedKid: null,
  isLoading: false,
  error: null,

  fetchProfiles: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get('/kids/profiles');
      set({ profiles: response.data.profiles, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Error al cargar perfiles',
        isLoading: false
      });
    }
  },

  fetchProfile: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get(`/kids/profiles/${id}`);
      set({ isLoading: false });
      return response.data;
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Error al cargar perfil',
        isLoading: false
      });
      throw error;
    }
  },

  createProfile: async (data: CreateKidProfileDTO) => {
    set({ isLoading: true, error: null });
    try {
      await api.post('/kids/profiles', data);
      await get().fetchProfiles(); // Recargar lista
      set({ isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Error al crear perfil',
        isLoading: false
      });
      throw error;
    }
  },

  updateProfile: async (id: number, data: UpdateKidProfileDTO) => {
    set({ isLoading: true, error: null });
    try {
      await api.put(`/kids/profiles/${id}`, data);
      await get().fetchProfiles(); // Recargar lista
      set({ isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Error al actualizar perfil',
        isLoading: false
      });
      throw error;
    }
  },

  deleteProfile: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`/kids/profiles/${id}`);
      await get().fetchProfiles(); // Recargar lista

      // Si era el perfil seleccionado, limpiar selección
      if (get().selectedKid?.id === id) {
        set({ selectedKid: null });
      }

      set({ isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Error al eliminar perfil',
        isLoading: false
      });
      throw error;
    }
  },

  selectKid: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await api.post(`/kids/select/${id}`);

      // Obtener datos del perfil seleccionado
      const profile = get().profiles.find(p => p.id === id);
      if (profile) {
        set({ selectedKid: profile, isLoading: false });
      } else {
        // Si no está en la lista, obtenerlo del servidor
        const response = await api.get(`/kids/profiles/${id}`);
        set({ selectedKid: response.data.profile, isLoading: false });
      }
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Error al seleccionar perfil',
        isLoading: false
      });
      throw error;
    }
  },

  getCurrentKid: async () => {
    try {
      const response = await api.get('/kids/current');
      set({ selectedKid: response.data.profile });
    } catch (error: any) {
      console.error('Error al obtener perfil actual:', error);
      set({ selectedKid: null });
    }
  },

  logoutKid: async () => {
    try {
      await api.post('/kids/logout');
      set({ selectedKid: null });
    } catch (error: any) {
      console.error('Error al cerrar sesión kid:', error);
      throw error;
    }
  },

  canPlay: async (kidId: number): Promise<CanPlayResponse> => {
    try {
      const response = await api.get(`/kids/${kidId}/can-play`);
      return response.data;
    } catch (error: any) {
      console.error('Error al verificar si puede jugar:', error);
      throw error;
    }
  },

  registerPlayTime: async (kidId: number, minutos: number) => {
    try {
      await api.post(`/kids/${kidId}/register-time`, {
        minutos_jugados: minutos
      });
    } catch (error: any) {
      console.error('Error al registrar tiempo de juego:', error);
      throw error;
    }
  },

  getStats: async (kidId: number): Promise<KidStats> => {
    try {
      const response = await api.get(`/kids/${kidId}/stats`);
      return response.data.stats;
    } catch (error: any) {
      console.error('Error al obtener estadísticas:', error);
      throw error;
    }
  },

  registerProgress: async (kidId: number, data: any) => {
    try {
      await api.post(`/kids/${kidId}/progress`, data);
    } catch (error: any) {
      console.error('Error al registrar progreso:', error);
      throw error;
    }
  },

  clearError: () => set({ error: null })
}), {
  name: 'neureka-kids',
  partialize: (state) => ({ selectedKid: state.selectedKid }),
}));
